import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, InputAdornment, CircularProgress,
  Alert, Card, CardContent, Chip, Divider, Grid, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Autocomplete, Avatar, Stack, Container, IconButton, Tooltip,
  Snackbar,
} from '@mui/material';
import {
  Search, Person, Phone, Email, LocationOn, Description,
  History, ArticleOutlined, FolderOpen, OpenInNew, Download,
  Image as ImageIcon, PictureAsPdf,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';
import API_URL from '../../../config/api';

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr.slice(0, 10)), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return dateStr; }
};

const moeda = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function SectionTitle({ icon, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <Box sx={{ color: '#ED3237', display: 'flex' }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={700}>{label}</Typography>
    </Stack>
  );
}

function StatusChip({ status }) {
  const map = { Ativo: 'success', Finalizado: 'default', Cancelado: 'error', Atrasado: 'error', Agendado: 'warning' };
  return <Chip label={status || '—'} color={map[status] || 'default'} size="small" />;
}

function ArquivoRow({ doc, clienteUuid, onErro }) {
  const [loading, setLoading] = useState(false);
  const isPdf = doc.tipo?.includes('pdf');
  const isImage = doc.tipo?.startsWith('image/');

  const abrirArquivo = async () => {
    setLoading(true);
    // abre a janela antes do await para não cair no popup blocker do browser
    const win = window.open('about:blank', '_blank');
    try {
      const res = await axios.get(
        `${API_URL}/clientes/${clienteUuid}/arquivos/${doc.id}`,
        { headers: authHeader() }
      );
      win.location.href = res.data.url;
    } catch {
      win.close();
      onErro('Erro ao abrir o arquivo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}
      sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      {isImage
        ? <ImageIcon fontSize="small" sx={{ color: '#ED3237' }} />
        : <PictureAsPdf fontSize="small" sx={{ color: '#ED3237' }} />
      }
      <Box flex={1}>
        <Typography variant="body2" fontWeight={500}>{doc.nome}</Typography>
        <Typography variant="caption" color="text.secondary">{doc.tipo}</Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{fmt(doc.data_envio)}</Typography>
      <Tooltip title={isPdf ? 'Abrir PDF' : 'Visualizar'}>
        <span>
          <IconButton size="small" onClick={abrirArquivo} disabled={loading} sx={{ color: '#ED3237' }}>
            {loading ? <CircularProgress size={16} /> : <OpenInNew fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function ContratoLocacaoRow({ contrato, onErro }) {
  const [loading, setLoading] = useState(false);

  const baixar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/contratos-locacao/download/${contrato.id}`,
        { headers: authHeader(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = contrato.nome_arquivo;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      onErro('Erro ao baixar o contrato.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}
      sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
      <ArticleOutlined fontSize="small" sx={{ color: '#ED3237' }} />
      <Box flex={1}>
        <Typography variant="body2" fontWeight={500}>{contrato.nome_arquivo}</Typography>
        {contrato.observacoes && (
          <Typography variant="caption" color="text.secondary">{contrato.observacoes}</Typography>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>{fmt(contrato.data_upload)}</Typography>
      <Tooltip title="Baixar contrato">
        <span>
          <IconButton size="small" onClick={baixar} disabled={loading} sx={{ color: '#ED3237' }}>
            {loading ? <CircularProgress size={16} /> : <Download fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

export default function FichaCliente() {
  const [clientes, setClientes] = useState([]);
  const [selecionado, setSelecionado] = useState(null);
  const [ficha, setFicha] = useState(null);
  const [loadingClientes, setLoadingClientes] = useState(true);
  const [loadingFicha, setLoadingFicha] = useState(false);
  const [erro, setErro] = useState('');
  const [snack, setSnack] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/clientes`, { headers: authHeader() })
      .then((r) => setClientes(r.data))
      .catch(() => setErro('Erro ao carregar clientes.'))
      .finally(() => setLoadingClientes(false));
  }, []);

  useEffect(() => {
    if (!selecionado) { setFicha(null); return; }
    setLoadingFicha(true);
    setErro('');
    axios.get(`${API_URL}/clientes/${selecionado.uuid_cliente}/ficha`, { headers: authHeader() })
      .then((r) => setFicha(r.data))
      .catch(() => setErro('Erro ao carregar ficha do cliente.'))
      .finally(() => setLoadingFicha(false));
  }, [selecionado]);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Ficha do Cliente</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Selecione um cliente para ver dados, documentos, contratos e histórico de aluguéis.
      </Typography>

      <Autocomplete
        options={clientes}
        getOptionLabel={(o) => `${o.nome}${o.cpf_cnpj ? ` — ${o.cpf_cnpj}` : ''}`}
        value={selecionado}
        onChange={(_, val) => setSelecionado(val)}
        loading={loadingClientes}
        noOptionsText="Nenhum cliente encontrado"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Buscar cliente"
            placeholder="Nome ou CPF/CNPJ..."
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start"><Search /></InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loadingClientes && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ mb: 3, maxWidth: 520 }}
      />

      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

      {loadingFicha && (
        <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>
      )}

      {!loadingFicha && !selecionado && !erro && (
        <Box display="flex" flexDirection="column" alignItems="center" mt={8} color="text.disabled">
          <Person sx={{ fontSize: 72, mb: 2, opacity: 0.2 }} />
          <Typography>Selecione um cliente para ver a ficha completa</Typography>
        </Box>
      )}

      {!loadingFicha && ficha && (
        <Stack spacing={3}>

          {/* Dados cadastrais */}
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Avatar sx={{ bgcolor: '#ED3237', width: 52, height: 52, fontSize: 22, fontWeight: 700 }}>
                  {ficha.cliente.nome?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{ficha.cliente.nome}</Typography>
                  <Typography variant="body2" color="text.secondary">{ficha.cliente.cpf_cnpj}</Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {ficha.cliente.telefone && (
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{ficha.cliente.telefone}</Typography>
                    </Stack>
                  </Grid>
                )}
                {ficha.cliente.email && (
                  <Grid item xs={12} sm={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{ficha.cliente.email}</Typography>
                    </Stack>
                  </Grid>
                )}
                {ficha.cliente.endereco && (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">
                        {ficha.cliente.endereco}{ficha.cliente.uf ? ` — ${ficha.cliente.uf}` : ''}
                      </Typography>
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          {/* Documentos */}
          <Card variant="outlined">
            <CardContent>
              <SectionTitle icon={<FolderOpen />} label={`Documentos (${ficha.documentos.length})`} />
              {ficha.documentos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum documento cadastrado.</Typography>
              ) : (
                <Stack spacing={1}>
                  {ficha.documentos.map((doc) => (
                    <ArquivoRow
                      key={doc.id}
                      doc={doc}
                      clienteUuid={selecionado.uuid_cliente}
                      onErro={setSnack}
                    />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Contratos de Locação */}
          <Card variant="outlined">
            <CardContent>
              <SectionTitle icon={<ArticleOutlined />} label={`Contratos de Locação (${ficha.contratosLocacao.length})`} />
              {ficha.contratosLocacao.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum contrato de locação enviado.</Typography>
              ) : (
                <Stack spacing={1}>
                  {ficha.contratosLocacao.map((c) => (
                    <ContratoLocacaoRow key={c.id} contrato={c} onErro={setSnack} />
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Histórico de Aluguéis */}
          <Card variant="outlined">
            <CardContent>
              <SectionTitle icon={<History />} label={`Histórico de Aluguéis (${ficha.alugueis.length})`} />
              {ficha.alugueis.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Nenhum aluguel registrado.</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell><strong>Veículo</strong></TableCell>
                        <TableCell><strong>Retirada</strong></TableCell>
                        <TableCell><strong>Devolução</strong></TableCell>
                        <TableCell><strong>Valor</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Pagamento</strong></TableCell>
                        <TableCell><strong>Contrato</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ficha.alugueis.map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{a.modelo || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{a.placa}</Typography>
                          </TableCell>
                          <TableCell>{fmt(a.data_retirada)}</TableCell>
                          <TableCell>{fmt(a.data_devolucao)}</TableCell>
                          <TableCell>{moeda(a.valor_total)}</TableCell>
                          <TableCell><StatusChip status={a.status_aluguel} /></TableCell>
                          <TableCell>
                            <Chip
                              label={a.status_pagamento || '—'}
                              size="small"
                              color={a.status_pagamento === 'Pago' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {a.contrato_status ? (
                              <Chip
                                label={a.contrato_status === 'assinado' ? 'Assinado' : 'Pendente'}
                                size="small"
                                color={a.contrato_status === 'assinado' ? 'success' : 'default'}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

        </Stack>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Container>
  );
}
